import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Database, Table, ChevronRight, ChevronDown, Key, Link2, Hash, BarChart3, Play, Clock, Terminal, Columns, List, Plus, Trash2, Edit3, Check, X, Maximize2, Minimize2, ArrowUpDown } from 'lucide-react';
import SharedPageHeader from '../components/layout/PageHeader';

const DbToggleContainer = styled.div`
  display: flex;
  gap: 8px;
  background: var(--surface);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--border);
  margin-bottom: 16px;
  width: fit-content;
`;
const DbBtn = styled.button`
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  cursor: pointer;
  transition: all 0.2s;
`;

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const PageContainer = styled.div`
  padding: 20px;
  height: 100%;
`;

const DatabaseLayout = styled.div`
  display: flex;
  gap: 16px;
  height: calc(100vh - 150px);
`;

/* ── LEFT SIDEBAR ── */
const Sidebar = styled.div`
  width: 280px;
  min-width: 280px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const SidebarSearch = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-primary);
  font-size: 12px;
  outline: none;
  &:focus { border-color: var(--primary); }
  &::placeholder { color: var(--text-muted); }
`;

const SidebarBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const CatHeader = styled.div`
  padding: 8px 10px;
  background: ${p => p.$editing ? 'var(--surface-hover)' : 'var(--surface)'};
  border: 1px solid var(--border);
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  transition: all .2s;
  &:hover { background: var(--surface-hover); border-color: var(--primary); }
`;

const CatTitle = styled.div`
  color: var(--primary);
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const CatBadge = styled.span`
  color: var(--text-muted);
  font-size: 11px;
  font-weight: normal;
`;

const TblItem = styled.div`
  padding: 8px 12px;
  margin-bottom: 4px;
  margin-left: 8px;
  background: ${p => p.$active ? 'var(--surface-hover)' : 'transparent'};
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all .15s;
  &:hover { background: var(--surface-hover); border-color: var(--primary); }
`;

const TblName = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TblMeta = styled.span`
  color: var(--text-muted);
  font-size: 11px;
  white-space: nowrap;
`;

/* ── RIGHT PANEL ── */
const MainPanel = styled.div`
  flex: 1;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
`;

const PanelHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
`;

const PanelTitle = styled.h2`
  color: var(--primary);
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
`;

const PanelStats = styled.div`
  display: flex;
  gap: 12px;
  color: var(--text-muted);
  font-size: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const StatChip = styled.span`
  background: var(--surface);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  color: var(--text-secondary);
  white-space: nowrap;
`;

/* ── TABS ── */
const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid var(--border);
  flex-shrink: 0;
  padding: 0 16px;
`;

const Tab = styled.button`
  padding: 10px 16px;
  background: none;
  border: none;
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-muted)'};
  font-weight: ${p => p.$active ? '700' : '500'};
  font-size: 13px;
  cursor: pointer;
  border-bottom: 2px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  margin-bottom: -2px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all .15s;
  &:hover { color: var(--primary); }
`;

/* ── DATA TABLE ── */
const TableWrap = styled.div`
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

const DTable = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: ${p => p.$compact ? '11px' : '13px'};
`;

const DTHead = styled.thead`
  background: var(--bg);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const DTh = styled.th`
  padding: ${p => p.$compact ? '6px 8px' : '10px 12px'};
  text-align: left;
  color: var(--primary);
  font-weight: 600;
  border-bottom: 2px solid var(--border);
  white-space: nowrap;
  max-width: ${p => p.$colWidth || 180}px;
  min-width: ${p => p.$compact ? '60px' : '80px'};
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
  position: relative;
  user-select: none;
`;

const DTd = styled.td`
  padding: ${p => p.$compact ? '4px 8px' : '8px 12px'};
  border-bottom: 1px solid var(--border);
  color: var(--text-primary);
  max-width: ${p => p.$colWidth || 180}px;
  min-width: ${p => p.$compact ? '60px' : '80px'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DTr = styled.tr`
  &:hover { background: var(--surface-hover); }
`;

/* ── SCHEMA VIEW ── */
const SchemaGrid = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const SchemaCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const SchemaCardHeader = styled.div`
  padding: 12px 16px;
  background: var(--bg);
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SchemaRow = styled.div`
  display: grid;
  grid-template-columns: 30px 200px 140px 70px 1fr 120px;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--border);
  align-items: center;
  font-size: 13px;
  &:last-child { border-bottom: none; }
  &:hover { background: var(--surface-hover); }
`;

const ColIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$pk ? '#f59e0b' : p.$fk ? '#3b82f6' : 'var(--text-muted)'};
`;

const ColName = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
`;

const ColType = styled.span`
  color: #22c55e;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
`;

const ColNullable = styled.span`
  color: ${p => p.$nullable ? '#f59e0b' : 'var(--text-muted)'};
  font-size: 11px;
  font-weight: 600;
`;

const ColDefault = styled.span`
  color: var(--text-muted);
  font-size: 11px;
  font-family: 'JetBrains Mono', monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColFK = styled.span`
  color: #3b82f6;
  font-size: 11px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IndexRow = styled.div`
  padding: 6px 16px;
  font-size: 11px;
  color: var(--text-muted);
  font-family: 'JetBrains Mono', monospace;
  border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }
`;

/* ── SQL QUERY ── */
const QueryArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 16px;
  gap: 12px;
`;

const QueryInput = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  resize: vertical;
  outline: none;
  &:focus { border-color: var(--primary); }
`;

const QueryActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Btn = styled.button`
  padding: 7px 14px;
  border-radius: 6px;
  border: 1px solid ${p => p.$variant === 'primary' ? 'var(--primary)' : 'var(--border)'};
  background: ${p => p.$variant === 'primary' ? 'var(--primary)' : 'var(--surface)'};
  color: ${p => p.$variant === 'primary' ? '#fff' : 'var(--text-primary)'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all .15s;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const QueryResultInfo = styled.div`
  font-size: 12px;
  color: var(--text-muted);
  display: flex;
  gap: 12px;
  align-items: center;
`;

const QueryResultArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const ErrorBox = styled.div`
  padding: 12px;
  background: rgba(239,68,68,.1);
  border: 1px solid rgba(239,68,68,.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 13px;
  font-family: 'JetBrains Mono', monospace;
`;

/* ── COLUMN STATS ── */
const StatsPanel = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const StatsCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all .15s;
  &:hover { border-color: var(--primary); }
`;

const StatsBar = styled.div`
  height: 6px;
  background: var(--border);
  border-radius: 3px;
  margin-top: 6px;
  overflow: hidden;
`;

const StatsFill = styled.div`
  height: 100%;
  background: var(--primary);
  border-radius: 3px;
  width: ${p => p.$pct}%;
  transition: width .3s;
`;

const TopValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
  font-size: 11px;
  color: var(--text-secondary);
`;

/* ── TOOLBAR ── */
const Toolbar = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
`;

const ColWidthSlider = styled.input`
  width: 80px;
  accent-color: var(--primary);
`;

const ToolLabel = styled.span`
  font-size: 11px;
  color: var(--text-muted);
  white-space: nowrap;
`;

/* ── LOADING / EMPTY ── */
const CenteredMsg = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--text-muted);
  font-size: 15px;
  gap: 8px;
`;

/* ── SECTION EDIT MODAL (inline) ── */
const InlineInput = styled.input`
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--primary);
  background: var(--bg);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  width: 140px;
  outline: none;
`;

/* ═══════════════════════════════════════════════
   DEFAULT SECTIONS CONFIG
   ═══════════════════════════════════════════════ */
const DEFAULT_SECTIONS = [
  {
    id: 'manager',
    name: 'QLICKR Manager',
    tables: [
      'app_settings', 'ai_usage_log', 'devices',
      'workflow_builder', 'workflow_builder_executions',
      'workflow_sequences', 'workflow_execution_steps',
    ]
  },
  {
    id: 'instagram',
    name: 'Instagram',
    tables: [
      'instagram_accounts', 'instagram_actions_log', 'instagram_follow_status',
      'instagram_followers_analysis', 'instagram_interact_lists', 'instagram_profile_visits',
      'instagram_profiles', 'instagram_profiles_info', 'instagram_sessions_info',
      'instagram_targets', 'instagram_unfollow_lists', 'instagram_accounts',
      'instagram_device_configs', 'running_scripts', 'workstations', 'schema_version',
    ]
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    tables: ['tiktok_accounts', 'tiktok_actions_log', 'tiktok_targets']
  },
  {
    id: 'youtube',
    name: 'YouTube',
    tables: ['youtube_accounts', 'youtube_actions_log', 'youtube_targets']
  },
  {
    id: 'twitter',
    name: 'Twitter / X',
    tables: ['twitter_accounts', 'twitter_actions_log', 'twitter_targets']
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    tables: ['linkedin_accounts', 'linkedin_actions_log', 'linkedin_targets']
  },
  {
    id: 'gmail',
    name: 'Gmail',
    tables: ['gmail_accounts', 'gmail_actions_log']
  },
  {
    id: 'google_maps',
    name: 'Google Maps',
    tables: ['google_maps_accounts', 'google_maps_actions_log', 'google_maps_targets']
  },
  {
    id: 'pinterest',
    name: 'Pinterest',
    tables: ['pinterest_accounts', 'pinterest_actions_log', 'pinterest_targets']
  },
  {
    id: 'discord',
    name: 'Discord',
    tables: ['discord_accounts', 'discord_actions_log']
  },
  {
    id: 'telegram',
    name: 'Telegram',
    tables: ['telegram_accounts', 'telegram_actions_log']
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    tables: ['whatsapp_accounts', 'whatsapp_actions_log']
  },
  {
    id: 'tinder',
    name: 'Tinder',
    tables: ['tinder_accounts', 'tinder_actions_log']
  },
  {
    id: 'bumble',
    name: 'Bumble',
    tables: ['bumble_accounts', 'bumble_actions_log']
  },
  {
    id: 'other',
    name: 'Autres',
    tables: []  // auto-catches everything not in above sections
  }
];

const STORAGE_KEY = 'db_explorer_sections';

function loadSections() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
       const parsed = JSON.parse(raw);
       // Force update if sections are from old layout (< 15 sections = old structure)
       if (!parsed || parsed.length < 10 || (parsed[0] && parsed[0].id === 'impulse-bot')) {
           return DEFAULT_SECTIONS;
       }
       return parsed;
    }
  } catch {}
  return DEFAULT_SECTIONS;
}

function saveSections(sections) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
}

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */
const DatabasePage = () => {
  const [dbMode, setDbMode] = useState(null); // 'sqlite' | 'postgres' | 'both'
  const [dbEngine, setDbEngine] = useState('sqlite'); // default to sqlite until mode loads
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [schemaData, setSchemaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState('data'); // data | schema | query | stats
  const [compact, setCompact] = useState(false);
  const [colWidth, setColWidth] = useState(160);
  const [searchFilter, setSearchFilter] = useState('');

  // ── Fetch db_mode from settings on mount ──
  useEffect(() => {
    fetch('/api/settings/db-mode')
      .then(r => r.json())
      .then(d => {
        const mode = d.mode || 'both';
        setDbMode(mode);
        if (mode === 'postgres') setDbEngine('postgres');
        else setDbEngine('sqlite'); // sqlite or both → default to sqlite
      })
      .catch(() => { setDbMode('sqlite'); setDbEngine('sqlite'); });
  }, []);
  const [expandedCats, setExpandedCats] = useState({});
  const [sections, setSections] = useState(loadSections);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [movingTable, setMovingTable] = useState(null); // table name being moved
  const [queryText, setQueryText] = useState('SELECT * FROM instagram_profile_visits LIMIT 20');
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [colStats, setColStats] = useState({}); // { colName: stats }
  const [, setLoadingStats] = useState(false);
  const [dataLimit, setDataLimit] = useState(100);

  // ── Load tables ──
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setTables([]);
        setSelectedTable(null);
        setTableData(null);
        setSchemaData(null);
        setColStats({});
        const r = await fetch(`/api/database/tables?db=${dbEngine}`);
        const data = await r.json();
        setTables(Array.isArray(data) ? data : []);
        // Auto-expand all categories
        const exp = {};
        sections.forEach(s => { exp[s.id] = true; });
        setExpandedCats(exp);
      } catch (e) {
        console.error('Error loading tables:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [dbEngine]); // eslint-disable-line

  // ── Categorize tables ──
  const categorizedTables = useCallback(() => {
    const assignedTables = new Set();
    sections.forEach(s => s.tables.forEach(t => assignedTables.add(t)));
    const result = sections.map(s => {
      const isLastSection = s.id === sections[sections.length - 1]?.id;
      const sectionTables = isLastSection && s.tables.length === 0
        ? tables.filter(t => !assignedTables.has(t.name))
        : tables.filter(t => s.tables.includes(t.name));
      return { ...s, resolvedTables: sectionTables };
    });
    return result;
  }, [tables, sections]);

  // ── Load table data ──
  const loadTableData = async (tableName, limit = dataLimit) => {
    try {
      setLoadingData(true);
      setSelectedTable(tableName);
      setSchemaData(null);
      setColStats({});
      const r = await fetch(`/api/database/table/${tableName}?limit=${limit}&db=${dbEngine}`);
      const data = await r.json();
      setTableData(data.columns ? data : null);
      // Also load schema
      loadSchema(tableName);
    } catch (e) {
      console.error('Error loading table data:', e);
    } finally {
      setLoadingData(false);
    }
  };

  const loadSchema = async (tableName) => {
    try {
      const r = await fetch(`/api/database/schema/${tableName}?db=${dbEngine}`);
      const data = await r.json();
      setSchemaData(data.columns ? data : null);
    } catch (e) {
      console.error('Error loading schema:', e);
    }
  };

  // ── Column stats ──
  const loadColumnStats = async (tableName, colName) => {
    if (colStats[colName]) return; // already loaded
    setLoadingStats(true);
    try {
      const r = await fetch(`/api/database/column-stats/${tableName}/${colName}?db=${dbEngine}`);
      const data = await r.json();
      setColStats(prev => ({ ...prev, [colName]: data }));
    } catch (e) {
      console.error('Error loading column stats:', e);
    } finally {
      setLoadingStats(false);
    }
  };

  // ── SQL Query ──
  const runQuery = async () => {
    setQueryError(null);
    setQueryResult(null);
    setQueryLoading(true);
    try {
      const r = await fetch('/api/database/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: queryText, db: dbEngine })
      });
      const data = await r.json();
      if (data.error) {
        setQueryError(data.error);
      } else {
        setQueryResult(data);
      }
    } catch (e) {
      setQueryError(e.message);
    } finally {
      setQueryLoading(false);
    }
  };

  // ── Section management ──
  const addSection = () => {
    const newId = `section_${Date.now()}`;
    const next = [...sections, { id: newId, name: 'Nouvelle section', tables: [] }];
    setSections(next);
    saveSections(next);
    setExpandedCats(prev => ({ ...prev, [newId]: true }));
  };

  const deleteSection = (sectionId) => {
    const next = sections.filter(s => s.id !== sectionId);
    setSections(next);
    saveSections(next);
  };

  const renameSection = (sectionId, newName) => {
    const next = sections.map(s => s.id === sectionId ? { ...s, name: newName } : s);
    setSections(next);
    saveSections(next);
    setEditingSectionId(null);
  };

  const moveTableToSection = (tableName, targetSectionId) => {
    const next = sections.map(s => ({
      ...s,
      tables: s.id === targetSectionId
        ? [...s.tables.filter(t => t !== tableName), tableName]
        : s.tables.filter(t => t !== tableName)
    }));
    setSections(next);
    saveSections(next);
    setMovingTable(null);
  };

  // ── Filter tables ──
  const filteredCats = categorizedTables().map(cat => ({
    ...cat,
    resolvedTables: cat.resolvedTables.filter(t =>
      !searchFilter || t.name.toLowerCase().includes(searchFilter.toLowerCase())
    )
  }));

  // ── Render ──
  return (
    <PageContainer>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <SharedPageHeader icon={Database} title="Base de Données" subtitle={`Visualiseur ${dbEngine === 'sqlite' ? 'SQLite' : 'PostgreSQL'}`} />
        <DbToggleContainer style={{ marginBottom: 0 }}>
          {(dbMode === 'postgres' || dbMode === 'both') && (
            <DbBtn $active={dbEngine === 'postgres'} onClick={() => setDbEngine('postgres')}>🐘 PostgreSQL</DbBtn>
          )}
          <DbBtn $active={dbEngine === 'sqlite'} onClick={() => setDbEngine('sqlite')}>🪶 SQLite</DbBtn>
        </DbToggleContainer>
      </div>

      <DatabaseLayout>
        {/* ═══ LEFT SIDEBAR ═══ */}
        <Sidebar>
          <SidebarHeader>
            <span style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 14 }}>
              Tables ({tables.length})
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <Btn onClick={addSection} title="Ajouter une section" style={{ padding: '4px 8px' }}>
                <Plus size={14} />
              </Btn>
              <Btn onClick={() => { setSections(DEFAULT_SECTIONS); saveSections(DEFAULT_SECTIONS); }} title="Réinitialiser" style={{ padding: '4px 8px' }}>
                <ArrowUpDown size={14} />
              </Btn>
            </div>
          </SidebarHeader>

          <SidebarSearch>
            <SearchInput
              placeholder="Rechercher une table..."
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
            />
          </SidebarSearch>

          <SidebarBody>
            {loading ? (
              <CenteredMsg>Chargement...</CenteredMsg>
            ) : (
              filteredCats.map(cat => (
                <div key={cat.id}>
                  <CatHeader $editing={editingSectionId === cat.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}
                      onClick={() => {
                        if (editingSectionId !== cat.id) setExpandedCats(p => ({ ...p, [cat.id]: !p[cat.id] }));
                      }}
                    >
                      {expandedCats[cat.id] ? <ChevronDown size={14} color="var(--primary)" /> : <ChevronRight size={14} color="var(--primary)" />}
                      {editingSectionId === cat.id ? (
                        <InlineInput
                          value={editingSectionName}
                          onChange={e => setEditingSectionName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') renameSection(cat.id, editingSectionName); if (e.key === 'Escape') setEditingSectionId(null); }}
                          autoFocus
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <CatTitle>
                          <Database size={14} />
                          {cat.name}
                          <CatBadge>({cat.resolvedTables.length})</CatBadge>
                        </CatTitle>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                      {editingSectionId === cat.id ? (
                        <>
                          <Btn style={{ padding: '2px 6px' }} onClick={() => renameSection(cat.id, editingSectionName)}><Check size={12} /></Btn>
                          <Btn style={{ padding: '2px 6px' }} onClick={() => setEditingSectionId(null)}><X size={12} /></Btn>
                        </>
                      ) : (
                        <>
                          <Btn style={{ padding: '2px 6px' }} onClick={(e) => { e.stopPropagation(); setEditingSectionId(cat.id); setEditingSectionName(cat.name); }} title="Renommer"><Edit3 size={11} /></Btn>
                          {sections.length > 1 && (
                            <Btn style={{ padding: '2px 6px' }} onClick={(e) => { e.stopPropagation(); deleteSection(cat.id); }} title="Supprimer"><Trash2 size={11} /></Btn>
                          )}
                        </>
                      )}
                    </div>
                  </CatHeader>

                  {expandedCats[cat.id] && cat.resolvedTables.map(t => (
                    <div key={t.name} style={{ position: 'relative' }}>
                      <TblItem
                        $active={selectedTable === t.name}
                        onClick={() => loadTableData(t.name)}
                        onContextMenu={(e) => { e.preventDefault(); setMovingTable(movingTable === t.name ? null : t.name); }}
                      >
                        <TblName>{t.name}</TblName>
                        <TblMeta>{t.row_count?.toLocaleString()} lignes</TblMeta>
                      </TblItem>
                      {movingTable === t.name && (
                        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 100, background: 'var(--card-bg)', border: '1px solid var(--primary)', borderRadius: 6, padding: 6, boxShadow: '0 8px 24px rgba(0,0,0,.4)', minWidth: 160 }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, padding: '2px 6px' }}>Déplacer vers :</div>
                          {sections.filter(s => s.id !== cat.id).map(s => (
                            <div key={s.id} onClick={() => moveTableToSection(t.name, s.id)}
                              style={{ padding: '6px 10px', cursor: 'pointer', borderRadius: 4, fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}
                              onMouseEnter={e => e.target.style.background = 'var(--surface-hover)'}
                              onMouseLeave={e => e.target.style.background = 'transparent'}
                            >
                              {s.name}
                            </div>
                          ))}
                          <div onClick={() => setMovingTable(null)} style={{ padding: '4px 10px', cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)', marginTop: 4 }}>
                            Annuler
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </SidebarBody>
        </Sidebar>

        {/* ═══ MAIN PANEL ═══ */}
        <MainPanel>
          {!selectedTable ? (
            <CenteredMsg>
              <Table size={48} style={{ opacity: 0.2 }} />
              <span>Sélectionnez une table pour explorer</span>
            </CenteredMsg>
          ) : loadingData ? (
            <CenteredMsg>Chargement...</CenteredMsg>
          ) : (
            <>
              {/* Header */}
              <PanelHeader>
                <PanelTitle>{selectedTable}</PanelTitle>
                <PanelStats>
                  {tableData && <StatChip>{tableData.total?.toLocaleString()} lignes</StatChip>}
                  {tableData && <StatChip>{tableData.columns?.length} colonnes</StatChip>}
                  {schemaData?.tableSize && <StatChip>{schemaData.tableSize}</StatChip>}
                </PanelStats>
              </PanelHeader>

              {/* Tabs */}
              <TabBar>
                <Tab $active={activeTab === 'data'} onClick={() => setActiveTab('data')}><List size={14} /> Données</Tab>
                <Tab $active={activeTab === 'schema'} onClick={() => setActiveTab('schema')}><Columns size={14} /> Structure</Tab>
                <Tab $active={activeTab === 'stats'} onClick={() => { setActiveTab('stats'); if (schemaData?.columns) schemaData.columns.forEach(c => loadColumnStats(selectedTable, c.column_name)); }}><BarChart3 size={14} /> Statistiques</Tab>
                <Tab $active={activeTab === 'query'} onClick={() => setActiveTab('query')}><Terminal size={14} /> SQL</Tab>
              </TabBar>

              {/* ── DATA TAB ── */}
              {activeTab === 'data' && tableData && (
                <>
                  <Toolbar>
                    <Btn $variant={compact ? 'primary' : undefined} onClick={() => setCompact(!compact)} style={{ padding: '4px 10px' }}>
                      {compact ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
                      <span style={{ fontSize: 11 }}>{compact ? 'Normal' : 'Compact'}</span>
                    </Btn>
                    <ToolLabel>Largeur col:</ToolLabel>
                    <ColWidthSlider type="range" min={60} max={300} value={colWidth} onChange={e => setColWidth(Number(e.target.value))} />
                    <ToolLabel>{colWidth}px</ToolLabel>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                      <ToolLabel>Lignes:</ToolLabel>
                      {[50, 100, 200, 500].map(n => (
                        <Btn key={n} $variant={dataLimit === n ? 'primary' : undefined}
                          style={{ padding: '3px 8px', fontSize: 11 }}
                          onClick={() => { setDataLimit(n); loadTableData(selectedTable, n); }}
                        >{n}</Btn>
                      ))}
                    </div>
                  </Toolbar>
                  <TableWrap>
                    <DTable $compact={compact}>
                      <DTHead>
                        <tr>
                          {tableData.columns.map(col => (
                            <DTh key={col.column_name} $compact={compact} $colWidth={colWidth} title={`${col.column_name} (${col.data_type})`}>
                              {col.column_name}
                              <div style={{ fontSize: 9, fontWeight: 'normal', color: 'var(--text-muted)', marginTop: 1 }}>{col.data_type}</div>
                            </DTh>
                          ))}
                        </tr>
                      </DTHead>
                      <tbody>
                        {tableData.data.map((row, i) => (
                          <DTr key={i}>
                            {tableData.columns.map(col => (
                              <DTd key={col.column_name} $compact={compact} $colWidth={colWidth} title={String(row[col.column_name] ?? '')}>
                                {row[col.column_name] !== null && row[col.column_name] !== undefined
                                  ? String(row[col.column_name])
                                  : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>NULL</span>
                                }
                              </DTd>
                            ))}
                          </DTr>
                        ))}
                      </tbody>
                    </DTable>
                  </TableWrap>
                </>
              )}

              {/* ── SCHEMA TAB ── */}
              {activeTab === 'schema' && schemaData && (
                <SchemaGrid>
                  <SchemaCard>
                    <SchemaCardHeader>
                      <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 14 }}>
                        Colonnes ({schemaData.columns.length})
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {schemaData.total.toLocaleString()} lignes {schemaData.tableSize && `\u2022 ${schemaData.tableSize}`}
                      </span>
                    </SchemaCardHeader>
                    {/* Column header */}
                    <SchemaRow style={{ background: 'var(--bg)', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)' }}>
                      <span>#</span>
                      <span>Colonne</span>
                      <span>Type</span>
                      <span>Null?</span>
                      <span>Défaut</span>
                      <span>Relation</span>
                    </SchemaRow>
                    {schemaData.columns.map((col, i) => (
                      <SchemaRow key={col.column_name}>
                        <ColIcon $pk={col.is_primary_key} $fk={!!col.foreign_key}>
                          {col.is_primary_key ? <Key size={13} /> : col.foreign_key ? <Link2 size={13} /> : <Hash size={11} />}
                        </ColIcon>
                        <ColName>{col.column_name}</ColName>
                        <ColType>
                          {col.udt_name || col.data_type}
                          {col.character_maximum_length ? `(${col.character_maximum_length})` : ''}
                        </ColType>
                        <ColNullable $nullable={col.is_nullable === 'YES'}>
                          {col.is_nullable === 'YES' ? 'NULL' : 'NOT NULL'}
                        </ColNullable>
                        <ColDefault title={col.column_default || ''}>
                          {col.column_default ? String(col.column_default).substring(0, 30) : '\u2014'}
                        </ColDefault>
                        <div>
                          {col.foreign_key ? (
                            <ColFK>
                              <Link2 size={10} />
                              <span
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => loadTableData(col.foreign_key.table)}
                              >
                                {col.foreign_key.table}.{col.foreign_key.column}
                              </span>
                            </ColFK>
                          ) : col.is_primary_key ? (
                            <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 600 }}>PK</span>
                          ) : '\u2014'}
                        </div>
                      </SchemaRow>
                    ))}
                  </SchemaCard>

                  {/* Indexes */}
                  {schemaData.indexes?.length > 0 && (
                    <SchemaCard>
                      <SchemaCardHeader>
                        <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 14 }}>
                          Index ({schemaData.indexes.length})
                        </span>
                      </SchemaCardHeader>
                      {schemaData.indexes.map((idx, i) => (
                        <IndexRow key={i}>
                          <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{idx.indexname}</span>
                          <div style={{ marginTop: 2, fontSize: 10, color: 'var(--text-muted)' }}>{idx.indexdef}</div>
                        </IndexRow>
                      ))}
                    </SchemaCard>
                  )}
                </SchemaGrid>
              )}

              {/* ── STATS TAB ── */}
              {activeTab === 'stats' && schemaData && (
                <StatsPanel>
                  {schemaData.columns.map(col => {
                    const st = colStats[col.column_name];
                    const pctFilled = st ? Math.round((st.nonNullCount / Math.max(st.totalCount, 1)) * 100) : null;
                    return (
                      <StatsCard key={col.column_name} onClick={() => loadColumnStats(selectedTable, col.column_name)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <ColName>{col.column_name}</ColName>
                            <ColType style={{ marginLeft: 8 }}>{col.udt_name || col.data_type}</ColType>
                          </div>
                          {st && (
                            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-muted)' }}>
                              <span>{pctFilled}% rempli</span>
                              <span>{st.distinctCount != null ? `${st.distinctCount} uniques` : ''}</span>
                              <span>{st.nullCount} nulls</span>
                            </div>
                          )}
                        </div>
                        {st && (
                          <>
                            <StatsBar><StatsFill $pct={pctFilled} /></StatsBar>
                            {st.topValues?.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>Top valeurs :</div>
                                {st.topValues.slice(0, 5).map((tv, i) => (
                                  <TopValueRow key={i}>
                                    <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {tv.val !== null ? String(tv.val).substring(0, 40) : 'NULL'}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>{Number(tv.cnt).toLocaleString()}</span>
                                  </TopValueRow>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                        {!st && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Cliquez pour charger les stats</div>}
                      </StatsCard>
                    );
                  })}
                </StatsPanel>
              )}

              {/* ── QUERY TAB ── */}
              {activeTab === 'query' && (
                <QueryArea>
                  <QueryInput
                    value={queryText}
                    onChange={e => setQueryText(e.target.value)}
                    placeholder="SELECT * FROM ..."
                    onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') runQuery(); }}
                  />
                  <QueryActions>
                    <Btn $variant="primary" onClick={runQuery} disabled={queryLoading || !queryText.trim()}>
                      <Play size={14} />
                      {queryLoading ? 'Exécution...' : 'Exécuter (Ctrl+Enter)'}
                    </Btn>
                    {queryResult && (
                      <QueryResultInfo>
                        <span>{queryResult.rowCount} lignes</span>
                        <span>\u2022</span>
                        <span><Clock size={12} style={{ verticalAlign: -2 }} /> {queryResult.duration}ms</span>
                      </QueryResultInfo>
                    )}
                  </QueryActions>
                  {queryError && <ErrorBox>{queryError}</ErrorBox>}
                  {queryResult && (
                    <QueryResultArea>
                      <DTable $compact>
                        <DTHead>
                          <tr>
                            {queryResult.columns.map(c => (
                              <DTh key={c} $compact $colWidth={colWidth}>{c}</DTh>
                            ))}
                          </tr>
                        </DTHead>
                        <tbody>
                          {queryResult.rows.map((row, i) => (
                            <DTr key={i}>
                              {queryResult.columns.map(c => (
                                <DTd key={c} $compact $colWidth={colWidth} title={String(row[c] ?? '')}>
                                  {row[c] !== null && row[c] !== undefined
                                    ? String(row[c])
                                    : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>NULL</span>
                                  }
                                </DTd>
                              ))}
                            </DTr>
                          ))}
                        </tbody>
                      </DTable>
                    </QueryResultArea>
                  )}
                </QueryArea>
              )}
            </>
          )}
        </MainPanel>
      </DatabaseLayout>
    </PageContainer>
  );
};

export default DatabasePage;
